import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FISERV_CONFIG, FiservConfig } from '../../fiserv.config';
import {
  FiservClinicTerminal,
  FiservPingResponse,
  FiservCardPaymentRequest,
  FiservManualPaymentRequest,
  FiservAuthorizationResponse,
} from '../../models/fiserv.models';

export interface FiservRegisterTerminalRequest {
  hsn: string;
  label: string;
  terminalModel: 'Clover' | 'Ingenico' | string;
}

@Injectable()
export class FiservPosPaymentService {
  private readonly base: string;

  constructor(
    private http: HttpClient,
    @Inject(FISERV_CONFIG) config: FiservConfig
  ) {
    this.base = config.apiUrl.replace(/\/$/, '');
  }

  // ─── Terminal management ──────────────────────────────────────────────────
  // clinicId NOT sent — derived from JWT server-side (same as NMI pattern)

  /**
   * Returns terminals registered for the current clinic from the DB.
   * Calls epatientlink bridge → FiservIntegration WebAPI.
   */
  getTerminals(): Observable<FiservClinicTerminal[]> {
    return this.http.get<FiservClinicTerminal[]>(
      `${this.base}/api/v1/FiservTerminal/GetTerminals`
    );
  }

  /**
   * Ping a terminal to check if it is online (no session needed).
   */
  pingTerminal(hsn: string): Observable<FiservPingResponse> {
    return this.http.post<FiservPingResponse>(
      `${this.base}/api/v1/FiservTerminal/PingTerminal`, { hsn }
    );
  }

  /**
   * Register a new terminal by inserting into ClinicTerminals DB.
   * HSN comes from the physical device label.
   */
  registerTerminal(req: FiservRegisterTerminalRequest): Observable<FiservClinicTerminal> {
    return this.http.post<FiservClinicTerminal>(
      `${this.base}/api/v1/FiservTerminal/RegisterTerminal`, req
    );
  }

  /**
   * Remove a terminal (soft delete — sets IsActive = false).
   */
  removeTerminal(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/api/v1/FiservTerminal/RemoveTerminal?id=${id}`
    );
  }

  // ─── Payments ─────────────────────────────────────────────────────────────

  /**
   * Process a card-present payment (tap/swipe/insert).
   * This HTTP call BLOCKS until the customer presents their card.
   * Angular should show a spinner — the Observable completes with the final result.
   * No polling. No confirm step. One call.
   *
   * @param req.amount CENTS integer — e.g. 150 for $1.50
   */
  processCardPayment(req: FiservCardPaymentRequest): Observable<FiservAuthorizationResponse> {
    return this.http.post<FiservAuthorizationResponse>(
      `${this.base}/api/v1/FiservTerminal/CardPayment`, req
    );
  }

  /**
   * Process a manual card entry payment (customer keys in card number on terminal).
   * Same blocking pattern as processCardPayment.
   *
   * @param req.amount CENTS integer
   */
  processManualPayment(req: FiservManualPaymentRequest): Observable<FiservAuthorizationResponse> {
    return this.http.post<FiservAuthorizationResponse>(
      `${this.base}/api/v1/FiservTerminal/ManualPayment`, req
    );
  }
}
